<template>
    <base-dialog :loading="loading" :value="dialog" :title="$t('common.last_seven_days')" :width="500" @input="onDialogInput"
        @close="hide">
        <skeleton-loader v-if="loading" type="rows" :count="5" />
        <template v-else>
            <div class="section-title">{{ $t('common.wellness') }}</div>
            <div v-if="!worstVariable" class="no-data-text">
                {{ $t('common.no_data_yet') }}
            </div>
            <div v-else class="item-row">
                <div class="item-date">
                    {{ $t('common.same_variable_q') }}
                </div>
                <div class="item-body ml-1">
                    {{ worstVariable.label }} ({{ worstVariable.count }} {{ $t('common.of') }} {{ worstVariable.total }})
                </div>
            </div>

            <v-divider class="my-3" />

            <div class="section-title">{{ $t('common.activities') }}</div>
            <div v-if="activities.length === 0" class="no-data-text">
                {{ $t('common.no_data_yet') }}
            </div>
            <div v-else>
                <div v-for="(a, i) in activities" :key="`a-${i}`" class="item-row">
                    <div class="item-date">{{ formatDate(a.date) }}</div>
                    <div class="item-body">
                        <div v-for="(line, j) in activityLines(a)" :key="j">{{ line }}</div>
                    </div>
                </div>
            </div>

            <v-divider class="my-3" />

            <div class="section-title">{{ $t('common.competences') }}</div>
            <div v-if="competences.length === 0" class="no-data-text">
                {{ $t('common.no_data_yet') }}
            </div>
            <div v-else>
                <div v-for="(c, i) in competences" :key="`c-${i}`" class="item-row">
                    <div class="item-date">{{ formatDate(c.rawDate && c.rawDate.full) }}</div>
                    <div class="item-body">
                        <div>{{ c.versus || '-' }}</div>
                        <div>{{ c.minutes || 0 }}'</div>
                    </div>
                </div>
            </div>
        </template>
    </base-dialog>
</template>

<script>
import { db } from "@/utils/Firebase";
import BaseDialog from "@/components/common/BaseDialog";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import { filterValidWellness, getWellnessScore } from "@/utils/Routine";

export default {
    name: "WellnessActivitiesDialog",
    components: { BaseDialog, SkeletonLoader },
    data() {
        return {
            dialog: false,
            loading: false,
            studentId: null,
            startDate: null,
            endDate: null,
            activities: [],
            competences: [],
            wellnessHistory: [],
            cache: {},
        }
    },
    computed: {
        wellnessVariables(vm = this) {
            return [
                { key: 'sleep_night', label: vm.$t('common.sleep_night') },
                { key: 'muscles_hurt', label: vm.$t('common.muscles_hurt') },
                { key: 'tired', label: vm.$t('common.tired') },
                { key: 'stressed', label: vm.$t('common.stressed') },
                { key: 'energy', label: vm.$t('common.energy') },
            ]
        },
        validHistory(vm = this) {
            return filterValidWellness(vm.wellnessHistory)
        },
        worstVariable(vm = this) {
            const freq = {}
            let total = 0
            for (let idx = 5; idx < vm.validHistory.length; idx++) {
                const scores = getWellnessScore(vm.validHistory, idx)
                let minIdx = 0
                for (let i = 1; i < scores.length; i++) {
                    if (scores[i] < scores[minIdx]) minIdx = i
                }
                const key = vm.wellnessVariables[minIdx].key
                freq[key] = (freq[key] || 0) + 1
                total++
            }
            let best = null
            for (const v of vm.wellnessVariables) {
                const c = freq[v.key] || 0
                if (c > 0 && (!best || c > best.count)) {
                    best = { key: v.key, label: v.label, count: c }
                }
            }
            if (!best) return null
            return { ...best, total }
        },
    },
    methods: {
        async show(studentId, clickedDate, lastWellness = []) {
            const vm = this
            vm.studentId = studentId
            vm.wellnessHistory = lastWellness || []
            vm.endDate = vm.$moment(clickedDate, 'DD-MM-YYYY').format('YYYY-MM-DD')
            vm.startDate = vm.$moment(vm.endDate).subtract(6, 'days').format('YYYY-MM-DD')
            vm.dialog = true
            await vm.load()
        },
        hide() {
            this.dialog = false
        },
        onDialogInput(val) {
            if (!val) this.hide()
        },
        async load() {
            const vm = this
            const key = `${vm.studentId}|${vm.endDate}`

            if (vm.cache[key]) {
                vm.activities = vm.cache[key].activities
                vm.competences = vm.cache[key].competences
                return
            }

            await vm.withLoading(async () => {
                vm.activities = []
                vm.competences = []

                const [activities, competences] = await Promise.all([
                    vm.loadActivities(),
                    vm.loadCompetences(),
                ])

                vm.activities = activities
                vm.competences = competences
                vm.cache[key] = { activities, competences }
            })
        },
        async loadActivities() {
            const vm = this
            const snap = await db.collection('activities')
                .where('studentId', '==', vm.studentId)
                .where('date', '>=', vm.startDate)
                .where('date', '<=', vm.endDate)
                .get()
            return snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
        },
        async loadCompetences() {
            const vm = this
            const snap = await db.collection('assignedRoutines')
                .where('studentId', '==', vm.studentId)
                .where('type', '==', 'competence')
                .where('rawDate.full', '>=', vm.startDate)
                .where('rawDate.full', '<=', vm.endDate)
                .get()
            return snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .sort((a, b) => (b.rawDate?.full || '').localeCompare(a.rawDate?.full || ''))
        },
        activityLines(a) {
            const vm = this
            const yes = vm.$t('common.yes')
            const no = vm.$t('common.no')
            const yn = (v) => v ? yes : no

            if (a.type === 'activity') {
                return [a.name, a.time, a.intensity].filter(Boolean)
            }
            if (a.type === 'travel') {
                return [
                    `${vm.$t('common.travel')}: ${a.duration || '-'}`,
                    `${vm.$t('common.drove')} ${yn(a.drove)}`,
                    `${vm.$t('common.was_stressful')} ${yn(a.wasStressful)}`,
                ]
            }
            if (a.type === 'illness') {
                return [
                    `${vm.$t('common.illness')}: ${a.illness || '-'}`,
                    `${vm.$t('common.drove')} ${yn(a.drove)}`,
                    `${vm.$t('common.was_stressful')} ${yn(a.wasStressful)}`,
                ]
            }
            return []
        },
        formatDate(date) {
            if (!date) return ''
            return this.$moment(date, 'YYYY-MM-DD').format('DD-MM-YYYY')
        },
    },
}
</script>

<style scoped>
.section-title {
    font-weight: 700;
    font-size: var(--fs-title);
    color: var(--v-accent-base);
    margin-bottom: 6px;
}

.item-row {
    display: flex;
    align-items: baseline;
    padding: 4px 0;
    font-size: var(--fs-body);
}

.item-date {
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    min-width: 95px;
}

.item-body {
    color: var(--v-txtSecondary-base);
    flex: 1;
}

.no-data-text {
    color: var(--v-txtMuted-base);
    font-size: var(--fs-caption);
    font-style: italic;
    padding: 4px 0;
}

.item-body > div {
    line-height: 1.4;
}
</style>
